import CustomLink from '@components/link'
import { type SanityPromoSettings } from '@data/sanity/queries/types/site'
import { LayoutContext } from '@lib/site-context'
import cx from 'classnames'
import { useRouter } from 'next/router'
import { memo, useContext, useEffect, useMemo, useRef } from 'react'
import { useResizeObserver } from 'usehooks-ts'

type PromoBarProps = SanityPromoSettings

const intersectionThresholds = [0, 0.25, 0.5, 0.75, 1]

const PromoBar = memo(
  ({ enabled, text, link, display, saleMode }: PromoBarProps) => {
    const { setPromoHeight, setPromoVisibleHeight } = useContext(LayoutContext)

    const ref = useRef<HTMLDivElement>(null)

    const router = useRouter()

    const type = useMemo(() => {
      return display?.trim()
    }, [display])

    const { height: resizeHeight } = useResizeObserver({ ref })

    useEffect(() => {
      if (resizeHeight) {
        setPromoHeight(resizeHeight)
      }
    }, [resizeHeight, setPromoHeight])

    useEffect(() => {
      const element = ref.current

      if (!element) {
        return
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          const visibleHeight = entry.intersectionRect.height
          setPromoVisibleHeight(visibleHeight)
        },
        { threshold: intersectionThresholds },
      )

      observer.observe(element)

      return () => {
        observer.disconnect()
      }
    }, [setPromoVisibleHeight])

    // Check that promo bar is enabled, type and text are given and the page matches, if type is homepage
    if (
      !enabled ||
      !type ||
      !text ||
      (type === 'home' && router.asPath !== '/')
    ) {
      return null
    }

    return (
      <div
        ref={ref}
        className={cx(
          'relative z-60 text-center whitespace-nowrap overflow-x-auto overflow-y-hidden',
          {
            'bg-accent text-black': !saleMode,
            'bg-darkRed text-white': saleMode,
          },
        )}
      >
        <div className="inline-flex px-8 py-2 text-xs">
          {!!link && (
            <CustomLink
              link={{
                page: link,
              }}
              className="block"
            >
              {text}
            </CustomLink>
          )}
          {!link && <>{text}</>}
        </div>
      </div>
    )
  },
)

PromoBar.displayName = 'PromoBar'

export default PromoBar
